/* eslint-disable max-lines */

import {
    ActionCreatorWithPayload,
    ActionCreatorWithPreparedPayload,
    createAction,
    isAsyncThunkAction,
} from '@reduxjs/toolkit'
import {
    AnticsClientInstance,
    AnticsContext,
    JitsiAnticsEvent,
    JitsiAnticsMetricContext,
    JitsiAnticsMetricFields,
} from 'antics/types'
import {
    NinchatActions,
    NinchatErrors,
    NinchatEvents,
    NinchatMessages,
    NinchatProperties,
} from './types'
import type {AppDispatch} from 'store'
import {NinchatActionType} from 'api/types/actions'
import NinchatJS from 'ninchat-js'
import {antics} from '../antics'
import {closeSession} from 'api/action/closeSession'
import {compact} from 'components/Jitsi/utils/connectivity'
import {getEnvironment} from '../appconfig'
import {ninchatEventActions} from './slice'
import {t} from 'i18next'
import {toast} from 'components/Toast'

export type NinchatActionParams<T> = Omit<T, 'action' | 'action_id'> & Partial<T>
export type NinchatActionPayload = {payload?: NinchatProperties.PayloadDataFrame[]}
export type NinchatEventActionPayload<T> = {event: T, payload: NinchatMessages.NinchatMessageProperties}

export interface NinchatSession {
    close: () => void
    onClose: (callback: () => void) => void
    onConnActive: (callback: (timestamp: NinchatProperties.Time) => void) => void
    onConnState: (callback: (state: NinchatProperties.ConnState) => void) => void
    onEvent: (callback: (event: NinchatEvents.NinchatEvent, payloadFrames: ArrayBuffer[]) => void) => void
    onLog: (callback: (message: NinchatProperties.LogMessage) => void) => void
    onSessionEvent: (callback: (event: NinchatEvents.NinchatSessionEvent) => void) => void
    open: () => void
    reconnect: () => void
    send: (params: NinchatActionParams<NinchatActions.NinchatAction>, payload?: NinchatProperties.PayloadDataFrame[]) => NinchatClientPromise<NinchatEvents.NinchatEvent>
    setAddress: (address?: NinchatProperties.Apihost) => void
    setHeader: (key: NinchatProperties.HttpHeaderKey, value: NinchatProperties.HttpHeaderValue) => void
    setParams: (params: NinchatActionParams<NinchatActions.NinchatCreateSessionAction>) => void
}

export interface NinchatClientPromise<T> extends Promise<T> {
    actionId?: NinchatProperties.ActionID
    then: <TResult1 = T, TResult2 = never, P = ArrayBuffer[], E = NinchatEvents.NinchatErrorEvent> (
        onfulfilled?: ((value: T, payload: P) => TResult1 | PromiseLike<TResult1>) | undefined | null,
        onrejected?: ((reason: E) => TResult2 | PromiseLike<TResult2>) | undefined | null,
        onnotified?: ((value: T, payload: P) => TResult1 | PromiseLike<TResult1>) | undefined | null,
    ) => Promise<TResult1 | TResult2>
}

export type NinchatClientPromiseValue = NinchatEvents.NinchatEvent | EventActionPayload | (NinchatEvents.NinchatEvent | EventActionPayload)[]

type EventActionPayload = NinchatEventActionPayload<NinchatEvents.NinchatEvent>

type SessionPromiseCallbacks = {
    reject: (reason?: NinchatEvents.NinchatErrorEvent | Error) => void
    resolve: (value: NinchatEvents.NinchatSessionCreatedEvent) => void
}

class NinchatClient {
    private static actionTypeName = 'CLIENT'

    private _connActive: NinchatProperties.ConnActive | undefined

    private _dispatch: AppDispatch | undefined

    private _session: NinchatSession | undefined

    private _antics

    private anticsClientInstance: AnticsClientInstance

    private anticsContext: AnticsContext

    private apiHost: NinchatProperties.Apihost | undefined

    private connActive: boolean | undefined

    private connActiveTrack: ReturnType<typeof setInterval> | undefined

    private connStartTime: number | null

    private CONN_ACTIVE_TRACK_INTERVAL_IN_MS = 5000

    private CONN_ACTIVE_TRACK_TIMEOUT_IN_MS = 60000

    private connState: NinchatProperties.ConnState | undefined

    private pendingSends = new Set<(reason?: unknown) => void>()

    private sessionPromise: Promise<NinchatEvents.NinchatSessionCreatedEvent> | undefined

    private sessionCreationTime: number | null

    private sessionPromiseCallbacks: SessionPromiseCallbacks | undefined

    private siteSecret: NinchatProperties.SiteSecret | undefined

    private customHandledActions: (string | undefined)[] = [NinchatActionType.NinchatCreateRealmAction, NinchatActionType.NinchatJoinChannelAction]

    private customHandledStack = new Map<NinchatProperties.ActionID, NinchatEvents.NinchatEvent[]>()

    private xUserAgent: string

    private CUSTOM_HANDLED_TIMEOUT_IN_MS = 1000

    public closeAction = createAction(`${NinchatClient.actionTypeName}/close`)

    public connActiveAction = createAction<boolean | undefined>(`${NinchatClient.actionTypeName}/connActive`)

    public connStateAction = createAction<NinchatProperties.ConnState | undefined>(`${NinchatClient.actionTypeName}/connState`)

    public logAction = createAction<NinchatProperties.LogMessage>(`${NinchatClient.actionTypeName}/log`)

    constructor() {
        const {
            anticsUrl,
            apiHost,
            siteSecret,
            variant,
            xUserAgent,
        } = getEnvironment()

        this.apiHost = apiHost
        this.connStartTime = null
        this.sessionCreationTime = null
        this.siteSecret = siteSecret
        this.xUserAgent = xUserAgent

        this._antics = antics()

        this.anticsClientInstance = this._antics.newClientInstance(anticsUrl, xUserAgent, variant)
        this.anticsContext = this.anticsClientInstance.newSessionContext()

        this.onClose = this.onClose.bind(this)
        this.onConnActive = this.onConnActive.bind(this)
        this.onConnState = this.onConnState.bind(this)
        this.onEvent = this.onEvent.bind(this)
        this.onLog = this.onLog.bind(this)
        this.onSessionEvent = this.onSessionEvent.bind(this)
    }

    private get session(): NinchatSession {
        if (!this._session) {
            this._session = NinchatJS.newSession()

            if (!this._session) {
                throw new Error('unable to create session object')
            }

            this._session.onClose(this.onClose)
            this._session.onConnActive(this.onConnActive)
            this._session.onConnState(this.onConnState)
            this._session.onEvent(this.onEvent)
            this._session.onLog(this.onLog)
            this._session.onSessionEvent(this.onSessionEvent)
        }

        return this._session
    }

    private set session(session: NinchatSession | undefined) {
        this._session = session
    }

    public get connected() {
        return this.connState === NinchatProperties.ConnStates.CONNECTED
    }

    public get connectionState() {
        return this.connState
    }

    public get connectionActive() {
        return this.connActive
    }

    public get dispatch() {
        if (!this._dispatch) {
            throw new Error('dispatch not set')
        }

        return this._dispatch
    }

    public set dispatch(dispatch) {
        this._dispatch = dispatch
    }

    private clearConnectionState() {
        this._connActive = undefined
        this.connActive = undefined
        this.connActiveTrack = clearInterval(this.connActiveTrack) as unknown as typeof this.connActiveTrack
        this.connState = undefined
    }

    private onClose() {
        this.pendingSends.forEach((reject) => reject({message: 'connection closed'}))
        this.pendingSends.clear()
        this.clearConnectionState()
        this.dispatch(this.connStateAction(undefined))
        this.dispatch(this.connActiveAction(undefined))
        this.dispatch(this.closeAction())
    }

    private onConnActive(timestamp: NinchatProperties.ConnActive) {
        this._connActive = timestamp

        if (!this.connActive) {
            this.connActive = true
            this.dispatch(this.connActiveAction(true))
        }
    }

    private onConnState(state: NinchatProperties.ConnState) {
        if (state !== this.connState) {
            if (this.connState === NinchatProperties.ConnStates.CONNECTED) {
                this.anticsContext.send({w: 'disconnected'})
            }

            switch (state) {
                case NinchatProperties.ConnStates.CONNECTING:
                    this._connActive = undefined
                    this.connActive = undefined
                    this.dispatch(this.connActiveAction(undefined))
                    this.connStartTime = new Date().getTime()
                    break

                case NinchatProperties.ConnStates.CONNECTED:
                    if (this.connStartTime !== null) {
                        this.anticsContext.send({
                            t: (new Date().getTime() - this.connStartTime) / 1000.0,
                            w: 'conn',
                        })
                    }
                    break

                case NinchatProperties.ConnStates.DISCONNECTED:
                    if (this.connState === NinchatProperties.ConnStates.CONNECTING) {
                        if (this.connStartTime !== null) {
                            this.anticsContext.send({
                                fail: true,
                                t: (new Date().getTime() - this.connStartTime) / 1000.0,
                                w: 'conn',
                            })
                            this.connStartTime = null
                        }
                    }
                    break
                default:
            }
        }

        this.connState = state

        this.dispatch(this.connStateAction(state))
    }

    private async onEvent(event: NinchatEvents.NinchatEvent, payloadFrames: ArrayBuffer[]) {
        if (typeof ninchatEventActions[event.event] === 'function') {
            const {action_id} = event as typeof event & {action_id?: NinchatProperties.ActionID}

            if (event.event === 'session_updated') {
                this.dispatch(ninchatEventActions.session_updated(event))
                return
            }

            if (action_id && this.customHandledStack.has(action_id)) {
                this.customHandledStack.get(action_id)!.push(event) // eslint-disable-line @typescript-eslint/no-non-null-assertion
                return
            }

            if ('action_id' in event) {
                return
            }

            const actionCreator = ninchatEventActions[event.event]

            type WithPayload = ActionCreatorWithPayload<NinchatEvents.NinchatEvent>
            type WithObjectPayload = ActionCreatorWithPayload<EventActionPayload>
            type WithPreparedPayload = ActionCreatorWithPreparedPayload<unknown[], NinchatEvents.NinchatEvent>

            try {
                if (payloadFrames.length) {
                    const payloadString = NinchatJS.stringifyFrame(payloadFrames[0])
                    const payloadObject = JSON.parse(payloadString)
                    if (isAsyncThunkAction(actionCreator)) {
                        await this.dispatch((<WithObjectPayload>actionCreator)({ // eslint-disable-line no-extra-parens
                            event,
                            payload: payloadObject,
                        }))

                    /*
                     * @deprecated no action creators are using prepared payloads
                     */
                    } else {
                        this.dispatch((<WithPreparedPayload>actionCreator)(event, payloadObject)) // eslint-disable-line no-extra-parens
                    }
                } else {
                    this.dispatch((<WithPayload>actionCreator)(event)) // eslint-disable-line no-extra-parens
                }
            } catch (error) {
                const cause = error instanceof Error ? error.toString() : error
                console.error(`failed to handle ${event.event} event (${cause})`)
            }
        } else {
            console.error(`unable to handle event (${event?.event || 'unknown'})`)
        }
    }

    private onLog(message: NinchatProperties.LogMessage) {
        this.dispatch(this.logAction(message))
    }

    private onSessionEvent(event: NinchatEvents.NinchatSessionEvent) {
        if (!this.sessionCreationTime) {
            const anticsEvent = {
                w: 'session_lost',
                ...event.event !== 'session_created' && {fail: true},
            }

            this.anticsContext.send(anticsEvent)
        }

        let duration = null

        if (this.sessionCreationTime) {
            duration = (new Date().getTime() - this.sessionCreationTime) / 1000.0
            this.sessionCreationTime = null
        }

        if (event.event === 'session_created') {
            if (this.sessionCreationTime) {
                const anticsEvent = {
                    a: 'create_session',
                    t: duration,
                }

                this.anticsContext.send(anticsEvent)

                this.sessionCreationTime = null
            } else {
                setTimeout(this.anticsContext.flush, 0)
                this.anticsContext = this.anticsClientInstance.newSessionContext()
            }
        } else if (event.event === 'error') {
            if (this.sessionCreationTime) {
                const anticsEvent = {
                    a: 'create_session',
                    fail: true,
                    t: duration,
                }

                this.anticsContext.send(anticsEvent)

                this.sessionCreationTime = null

                setTimeout(this.anticsContext.flush, 0)
                this.anticsContext = this.anticsClientInstance.newSessionContext()
            }
        }

        if (this.sessionPromiseCallbacks) {
            if (event.event === 'error') {
                this.sessionPromiseCallbacks.reject(event)
            } else {
                this.sessionPromiseCallbacks.resolve(event)
            }
            this.sessionPromise = undefined
            this.sessionPromiseCallbacks = undefined
        } else if (typeof ninchatEventActions[event.event] === 'function') {
            this.dispatch(ninchatEventActions[event.event](event))

            if (event.event === 'error') {
                this.dispatch(closeSession())
                switch (event.error_type) {
                    case NinchatErrors.NinchatErrorType.NinchatAccessExpiredError:
                        toast.error(t('Access expired'))
                        break
                    default:
                        toast.error(t('Session error'))
                }
            }
        } else {
            console.error(`unable to handle session event (${event?.event || 'unknown'})`)
        }
    }

    private trackConnActive() {
        if (this._connActive === undefined) {
            return
        }

        const now = Date.now()

        if (this.connActive !== false && now - this._connActive > this.CONN_ACTIVE_TRACK_TIMEOUT_IN_MS) {
            this.connActive = false
            this.dispatch(this.connActiveAction(false))
            console.warn(`connection has been inactive for ${(now - this._connActive) / 1000} seconds`)
        }
    }

    public async call(params: NinchatActionParams<NinchatActions.NinchatAction>) {
        const _params = {
            ...params,
            ...this.siteSecret && {site_secret: this.siteSecret},
        }

        const [event] = await NinchatJS.call(_params, this.onLog, this.apiHost)

        if (event.event === 'error') {
            return Promise.reject(event)
        }

        return Promise.resolve(event)
    }

    public sendJitsiMetric(
        event: JitsiAnticsEvent,
        context: JitsiAnticsMetricContext = {},
        fields: JitsiAnticsMetricFields = {},
    ) {
        if (!event) {
            return
        }

        this.anticsContext.send(compact({
            e: event,
            ...context,
            ...fields,
            w: 'jitsi',
        }))
    }

    public close() {
        this.anticsContext.flush()

        this.session.close()
        this.session = undefined

        this.sessionPromise = undefined
        this.sessionPromiseCallbacks = undefined

        this.clearConnectionState()
    }

    public open(params: NinchatActionParams<NinchatActions.NinchatCreateSessionAction>): Promise<NinchatEvents.NinchatSessionCreatedEvent> {
        if (this.sessionPromise) {
            return this.sessionPromise
        } else if (this.connState === NinchatProperties.ConnStates.CONNECTING) {
            return Promise.reject(new Error('already connecting'))
        }

        if (this.connected) {
            return Promise.reject(new Error('already connected'))
        }

        void this.dispatch // eslint-disable-line no-void

        const {xUserAgent} = getEnvironment()
        this.xUserAgent = xUserAgent
        this.sessionCreationTime = new Date().getTime()

        this.session.setAddress(this.apiHost)
        this.session.setHeader('X-User-Agent', this.xUserAgent)
        this.session.setParams({
            ...{
                ...params,
                metadata: {client_instance: this.anticsClientInstance.clientInstance},
            },
            ...this.siteSecret && {site_secret: this.siteSecret},
        })
        this.session.open()

        this.connActiveTrack = setInterval(() => this.trackConnActive(), this.CONN_ACTIVE_TRACK_INTERVAL_IN_MS)

        const promise: Promise<NinchatEvents.NinchatSessionCreatedEvent> = new Promise((resolve, reject) => {
            this.sessionPromiseCallbacks = {
                reject,
                resolve,
            }
        })

        this.sessionPromise = promise

        return promise
    }

    public reconnect() {
        this.session.reconnect()
    }

    public send(params: NinchatActionParams<NinchatActions.NinchatAction>, payload?: NinchatProperties.PayloadDataFrame[]): NinchatClientPromise<NinchatClientPromiseValue> {
        if (!this.connected) {
            return Promise.reject('not connected') as NinchatClientPromise<NinchatEvents.NinchatErrorEvent> // eslint-disable-line prefer-promise-reject-errors
        }

        const notified: NinchatClientPromiseValue = []

        const ninchatJSPromise = this.session.send(params, payload)
        let pendingReject: ((reason?: unknown) => void) | null = null

        const promise = new Promise((resolve, reject) => {
            this.pendingSends.add(reject)
            pendingReject = reject
            ninchatJSPromise.then(
                (event: NinchatEvents.NinchatEvent, payloadFrames: ArrayBuffer[]) => {
                    if (ninchatJSPromise.actionId && this.customHandledActions.includes(params.action)) {
                        const action_id = ninchatJSPromise.actionId

                        this.customHandledStack.set(action_id, [])
                        setTimeout(() => {
                            const events = this.customHandledStack.get(action_id)
                            if (events && events.length) {
                                resolve(events)
                            } else {
                                reject({message: 'unable to handle action reply events'}) // eslint-disable-line prefer-promise-reject-errors
                            }
                            this.customHandledStack.delete(action_id)
                        }, this.CUSTOM_HANDLED_TIMEOUT_IN_MS)

                        return
                    }

                    try {
                        if (payloadFrames.length) {
                            const payloadString = NinchatJS.stringifyFrame(payloadFrames[0])
                            const payloadObject = JSON.parse(payloadString)
                            const actionPayload = {
                                event,
                                payload: payloadObject,
                            }
                            notified.length ? resolve([...notified, actionPayload]) : resolve(actionPayload) // eslint-disable-line no-unused-expressions
                        } else {
                            notified.length ? resolve([...notified, event]) : resolve(event) // eslint-disable-line no-unused-expressions
                        }
                    } catch {} // eslint-disable-line no-empty
                },
                (error: NinchatEvents.NinchatErrorEvent) => reject(error),
                (event: NinchatEvents.NinchatEvent, payloadFrames: ArrayBuffer[]) => {
                    try {
                        if (payloadFrames.length) {
                            const payloadString = NinchatJS.stringifyFrame(payloadFrames[0])
                            const payloadObject = JSON.parse(payloadString)
                            const actionPayload = {
                                event,
                                payload: payloadObject,
                            }
                            notified.push(actionPayload)
                        } else {
                            notified.push(event)
                        }
                    } catch {} // eslint-disable-line no-empty
                },
            )
        }) as NinchatClientPromise<NinchatClientPromiseValue>

        promise.then(
            () => {
                if (pendingReject) {
                    this.pendingSends.delete(pendingReject)
                }
            },
            () => {
                if (pendingReject) {
                    this.pendingSends.delete(pendingReject)
                }
            },
        )

        promise.actionId = ninchatJSPromise.actionId

        return promise
    }
}

export default new NinchatClient()

// vim:foldmethod=syntax
